/**
 * Phase 2 travel ball admin API client — organization, seasons, invites CRUD, coach management and
 * team-coach assignment. Also the public program signup and invite redemption endpoints. Mirrors
 * the shape of `services/travelBall.ts`: refusals bubble up as `TravelBallAdminError` whose `code`
 * comes from the response body, so pages branch on the server's own vocabulary.
 */

import { ApiError, del, get, patch, post } from './api';
import type {
  AssignTravelBallTeamCoachRequest,
  CreateTravelBallInviteRequest,
  CreateTravelBallProgramSignupRequest,
  CreateTravelBallSeasonRequest,
  RedeemTravelBallInviteRequest,
  RedeemTravelBallInviteResponse,
  TravelBallAdminErrorCode,
  TravelBallCoachesPage,
  TravelBallInvite,
  TravelBallInviteSendOutcome,
  TravelBallOrganization,
  TravelBallProgramSignupResponse,
  TravelBallSeason,
  TravelBallSeasonRolloverResponse,
  TravelBallTeamCoach,
  UpdateTravelBallOrganizationRequest,
  UpdateTravelBallSeasonRequest,
} from '../types/travelBallAdmin';

export const RATE_LIMITED_MESSAGE = 'Too many attempts, try again later.';
export const NETWORK_MESSAGE =
  'We could not reach RosterPilot. Check your connection and try again.';
export const UNKNOWN_MESSAGE = 'Something went wrong on our end. Please try again.';

/** A refused Phase 2 admin / invite / signup call. `message` is always displayable. */
export class TravelBallAdminError extends Error {
  readonly code:
    | TravelBallAdminErrorCode
    | 'rate_limited'
    | 'unauthorized'
    | 'forbidden'
    | 'network'
    | 'unknown';
  readonly status: number;
  /** The server's pause message when `code === 'travel_ball_organization_paused'`. */
  readonly pauseMessage: string | null;

  constructor(
    code: TravelBallAdminError['code'],
    message: string,
    status: number,
    pauseMessage: string | null = null,
  ) {
    super(message);
    this.name = 'TravelBallAdminError';
    this.code = code;
    this.status = status;
    this.pauseMessage = pauseMessage;
  }
}

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

const toError = (status: number, body: unknown): TravelBallAdminError => {
  const record = (body ?? {}) as { code?: unknown; message?: unknown };
  const bodyCode = typeof record.code === 'string' ? record.code : null;
  const message = text(record.message);

  if (status === 429) {
    return new TravelBallAdminError('rate_limited', RATE_LIMITED_MESSAGE, status);
  }
  if (status === 401) {
    return new TravelBallAdminError('unauthorized', message ?? UNKNOWN_MESSAGE, status);
  }

  // A pause 403 carries the server's copy and a specific code so the UI can show it rather than
  // the generic forbidden fallback.
  if (status === 403 && bodyCode === 'travel_ball_organization_paused') {
    return new TravelBallAdminError(
      'travel_ball_organization_paused',
      message ?? 'This travel ball program is paused.',
      status,
      message,
    );
  }

  if (status === 403) {
    return new TravelBallAdminError('forbidden', message ?? UNKNOWN_MESSAGE, status);
  }

  if (status === 404) {
    // 404 might be a specific row (invite_not_found, team_not_found, etc.) OR the whole feature off
    // (RequireFeature returns bare "Not found."). Prefer the server's code when present.
    if (bodyCode) {
      return new TravelBallAdminError(bodyCode as TravelBallAdminErrorCode, message ?? UNKNOWN_MESSAGE, status);
    }
    return new TravelBallAdminError(
      'travel_ball_disabled',
      message ?? 'Travel ball is not yet available.',
      status,
    );
  }

  if (bodyCode) {
    return new TravelBallAdminError(bodyCode as TravelBallAdminErrorCode, message ?? UNKNOWN_MESSAGE, status);
  }
  return new TravelBallAdminError('unknown', message ?? UNKNOWN_MESSAGE, status);
};

const fromApiError = (error: unknown): TravelBallAdminError => {
  if (error instanceof TravelBallAdminError) return error;
  if (error instanceof ApiError) {
    if (error.statusCode === 0) {
      return new TravelBallAdminError('network', NETWORK_MESSAGE, 0);
    }
    return toError(error.statusCode, error.response);
  }
  return new TravelBallAdminError('unknown', UNKNOWN_MESSAGE, 0);
};

// ────────────────────────────────────────────────────────────────────────────
// Organization

export const getCurrentOrganization = async (): Promise<TravelBallOrganization> => {
  try {
    return await get<TravelBallOrganization>('/api/travel-ball/organizations/current');
  } catch (err) {
    throw fromApiError(err);
  }
};

export const updateCurrentOrganization = async (
  request: UpdateTravelBallOrganizationRequest,
): Promise<TravelBallOrganization> => {
  try {
    return await patch<TravelBallOrganization>('/api/travel-ball/organizations/current', request);
  } catch (err) {
    throw fromApiError(err);
  }
};

// ────────────────────────────────────────────────────────────────────────────
// Seasons

export interface ListSeasonsOptions {
  /** Add archived (frozen, read-only) seasons. The coach surface's switcher opts in. */
  includeArchived?: boolean;
  /**
   * Add inactive-non-archived "planning draft" seasons. Admin UI only; the server also enforces
   * that non-admins never receive these regardless of the flag, so this is a request hint, not a
   * trust gate.
   */
  includeInactiveFuture?: boolean;
}

export const listSeasons = async (
  options: ListSeasonsOptions = {},
): Promise<TravelBallSeason[]> => {
  try {
    const params = new URLSearchParams();
    if (options.includeArchived) params.set('includeArchived', 'true');
    if (options.includeInactiveFuture) params.set('includeInactiveFuture', 'true');
    const query = params.toString();
    const path = query.length === 0
      ? '/api/travel-ball/seasons'
      : `/api/travel-ball/seasons?${query}`;
    return await get<TravelBallSeason[]>(path);
  } catch (err) {
    throw fromApiError(err);
  }
};

export const createSeason = async (
  request: CreateTravelBallSeasonRequest,
): Promise<TravelBallSeason> => {
  try {
    return await post<TravelBallSeason>('/api/travel-ball/seasons', request);
  } catch (err) {
    throw fromApiError(err);
  }
};

export const updateSeason = async (
  seasonId: string,
  request: UpdateTravelBallSeasonRequest,
): Promise<TravelBallSeason> => {
  try {
    return await patch<TravelBallSeason>(`/api/travel-ball/seasons/${seasonId}`, request);
  } catch (err) {
    throw fromApiError(err);
  }
};

export const activateSeason = async (seasonId: string): Promise<TravelBallSeason> => {
  try {
    return await post<TravelBallSeason>(`/api/travel-ball/seasons/${seasonId}/activate`, {});
  } catch (err) {
    throw fromApiError(err);
  }
};

export const deactivateSeason = async (seasonId: string): Promise<TravelBallSeason> => {
  try {
    return await post<TravelBallSeason>(`/api/travel-ball/seasons/${seasonId}/deactivate`, {});
  } catch (err) {
    throw fromApiError(err);
  }
};

export const archiveSeason = async (seasonId: string): Promise<TravelBallSeason> => {
  try {
    return await post<TravelBallSeason>(`/api/travel-ball/seasons/${seasonId}/archive`, {});
  } catch (err) {
    throw fromApiError(err);
  }
};

/** Target season id in the path first, source second (matches the controller route). */
export const rolloverSeason = async (
  targetSeasonId: string,
  sourceSeasonId: string,
): Promise<TravelBallSeasonRolloverResponse> => {
  try {
    return await post<TravelBallSeasonRolloverResponse>(
      `/api/travel-ball/seasons/${targetSeasonId}/rollover-from/${sourceSeasonId}`,
      {},
    );
  } catch (err) {
    throw fromApiError(err);
  }
};

// ────────────────────────────────────────────────────────────────────────────
// Invites + coaches

export const getCoachesPage = async (): Promise<TravelBallCoachesPage> => {
  try {
    return await get<TravelBallCoachesPage>('/api/travel-ball/coaches');
  } catch (err) {
    throw fromApiError(err);
  }
};

export const removeCoach = async (userId: string): Promise<void> => {
  try {
    await del<void>(`/api/travel-ball/coaches/${userId}`);
  } catch (err) {
    throw fromApiError(err);
  }
};

export const listInvites = async (): Promise<TravelBallInvite[]> => {
  try {
    return await get<TravelBallInvite[]>('/api/travel-ball/invites');
  } catch (err) {
    throw fromApiError(err);
  }
};

/**
 * A 502 `invite_send_failed` is NOT a failure to save: the invite exists with a live code and only
 * the email did not go out (the college "saved, not sent"). It comes back as an outcome with
 * `emailSent: false` so the page can say so and offer Resend, rather than as a thrown error.
 */
const sendOutcome = (err: unknown): TravelBallInviteSendOutcome | null => {
  if (!(err instanceof ApiError) || err.statusCode !== 502) return null;
  const body = (err.response ?? {}) as { code?: unknown; message?: unknown; invite?: unknown };
  if (body.code !== 'invite_send_failed' || !body.invite || typeof body.invite !== 'object') {
    return null;
  }
  return {
    invite: body.invite as TravelBallInvite,
    emailSent: false,
    message:
      text(body.message) ?? 'The invite was saved but the email could not be sent. Try resending it.',
  };
};

/**
 * Invites a coach. The API emails them a link and the code; neither ever comes back here. An
 * address that already has an open invite is re-armed and re-sent (200) rather than refused.
 */
export const createInvite = async (
  request: CreateTravelBallInviteRequest,
): Promise<TravelBallInviteSendOutcome> => {
  try {
    const invite = await post<TravelBallInvite>('/api/travel-ball/invites', request);
    return { invite, emailSent: true, message: null };
  } catch (err) {
    const outcome = sendOutcome(err);
    if (outcome) return outcome;
    throw fromApiError(err);
  }
};

/** Emails a NEW code (the previous email's code stops working) and resets the 14-day expiry. */
export const resendInvite = async (inviteId: string): Promise<TravelBallInviteSendOutcome> => {
  try {
    const invite = await post<TravelBallInvite>(`/api/travel-ball/invites/${inviteId}/resend`, {});
    return { invite, emailSent: true, message: null };
  } catch (err) {
    const outcome = sendOutcome(err);
    if (outcome) return outcome;
    throw fromApiError(err);
  }
};

export const revokeInvite = async (inviteId: string): Promise<void> => {
  try {
    await del<void>(`/api/travel-ball/invites/${inviteId}`);
  } catch (err) {
    throw fromApiError(err);
  }
};

// ────────────────────────────────────────────────────────────────────────────
// Team-coach assignments

export const listTeamCoaches = async (teamId: string): Promise<TravelBallTeamCoach[]> => {
  try {
    return await get<TravelBallTeamCoach[]>(`/api/travel-ball/teams/${teamId}/coaches`);
  } catch (err) {
    throw fromApiError(err);
  }
};

export const assignTeamCoach = async (
  teamId: string,
  request: AssignTravelBallTeamCoachRequest,
): Promise<TravelBallTeamCoach> => {
  try {
    return await post<TravelBallTeamCoach>(`/api/travel-ball/teams/${teamId}/coaches`, request);
  } catch (err) {
    throw fromApiError(err);
  }
};

export const unassignTeamCoach = async (teamId: string, userId: string): Promise<void> => {
  try {
    await del<void>(`/api/travel-ball/teams/${teamId}/coaches/${userId}`);
  } catch (err) {
    throw fromApiError(err);
  }
};

// ────────────────────────────────────────────────────────────────────────────
// Public: program signup + invite redemption

export const createProgramSignup = async (
  request: CreateTravelBallProgramSignupRequest,
): Promise<TravelBallProgramSignupResponse> => {
  try {
    return await post<TravelBallProgramSignupResponse>(
      '/api/public/travel-ball/signup/program',
      { ...request, platform: 'web' },
    );
  } catch (err) {
    throw fromApiError(err);
  }
};

export const redeemInvite = async (
  request: RedeemTravelBallInviteRequest,
): Promise<RedeemTravelBallInviteResponse> => {
  try {
    return await post<RedeemTravelBallInviteResponse>(
      '/api/public/travel-ball/invites/redeem',
      { ...request, platform: 'web' },
    );
  } catch (err) {
    throw fromApiError(err);
  }
};
